import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  type RowData,
  useReactTable,
} from '@tanstack/react-table'
import { useWindowVirtualizer } from '@tanstack/react-virtual'
import {
  type ReactNode,
  type RefObject,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { useInView } from 'react-intersection-observer'

import Loader from '@/components/Atoms/Loader/Loader'
import { ScreenSizeContext } from '@/providers/ScreenSizeProvider'

import cn from './CatalogApprovalVirtualTable.module.scss'

const INITIAL_VISIBLE_ROWS = 60
const LOAD_MORE_CHUNK = 60

export type CatalogApprovalColumn<T extends RowData> = ColumnDef<T> & {
  width: string
}

type CatalogApprovalVirtualTableProps<T extends RowData> = {
  columns: CatalogApprovalColumn<T>[]
  data: T[]
  emptyMessage: string
  getRowClassName?: (record: T) => string | undefined
  estimateRowHeight?: number
  minTableWidth?: string
  onRowClick?: (record: T) => void
  renderMobileCard?: (record: T) => ReactNode
  rowKey: (record: T) => string
  stickyBoundaryRef?: RefObject<HTMLElement>
}

function CatalogApprovalVirtualTable<T extends RowData>(
  props: CatalogApprovalVirtualTableProps<T>
) {
  const {
    columns,
    data,
    emptyMessage,
    getRowClassName,
    estimateRowHeight = 68,
    minTableWidth = '960px',
    onRowClick,
    renderMobileCard,
    rowKey,
    stickyBoundaryRef,
  } = props

  const [visibleCount, setVisibleCount] = useState(() =>
    Math.min(data.length, INITIAL_VISIBLE_ROWS)
  )
  const [tableWidth, setTableWidth] = useState(0)

  const parentRef = useRef<HTMLDivElement | null>(null)
  const tableInnerRef = useRef<HTMLDivElement | null>(null)
  const originalHeaderRef = useRef<HTMLDivElement | null>(null)
  const floatingHeaderRef = useRef<HTMLDivElement | null>(null)
  const isFloatingVisibleRef = useRef(false)
  const screen = useContext(ScreenSizeContext)
  const isMobile = !!screen?.xs || !!screen?.sm

  useEffect(() => {
    setVisibleCount(Math.min(data.length, INITIAL_VISIBLE_ROWS))
  }, [data])

  const visibleRows = useMemo(
    () => data.slice(0, visibleCount),
    [data, visibleCount]
  )

  const hasMore = visibleCount < data.length

  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0.1,
    rootMargin: '240px',
  })

  useEffect(() => {
    if (!inView || !hasMore) return

    setVisibleCount((current) =>
      Math.min(current + LOAD_MORE_CHUNK, data.length)
    )
  }, [data.length, hasMore, inView, visibleCount])

  const table = useReactTable({
    data: visibleRows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  const rows = table.getRowModel().rows

  const rowVirtualizer = useWindowVirtualizer({
    count: rows.length,
    estimateSize: () => estimateRowHeight,
    overscan: 16,
    scrollMargin: parentRef.current?.offsetTop ?? 0,
  })

  const gridTemplateColumns = useMemo(
    () => columns.map((column) => column.width).join(' '),
    [columns]
  )

  useEffect(() => {
    if (!tableInnerRef.current || !parentRef.current) return

    let frameId = 0

    const updateWidth = () => {
      if (!tableInnerRef.current || !parentRef.current) return

      const minWidth = Number.parseInt(minTableWidth, 10) || 0
      const contentBasedWidth = tableInnerRef.current.scrollWidth
      const containerWidth = parentRef.current.clientWidth

      setTableWidth(Math.max(minWidth, contentBasedWidth, containerWidth))
    }

    const scheduleWidthUpdate = () => {
      cancelAnimationFrame(frameId)
      frameId = requestAnimationFrame(updateWidth)
    }

    scheduleWidthUpdate()

    const resizeObserver = new ResizeObserver(scheduleWidthUpdate)
    resizeObserver.observe(tableInnerRef.current)
    resizeObserver.observe(parentRef.current)
    window.addEventListener('resize', scheduleWidthUpdate)
    window.visualViewport?.addEventListener('resize', scheduleWidthUpdate)

    return () => {
      cancelAnimationFrame(frameId)
      resizeObserver.disconnect()
      window.removeEventListener('resize', scheduleWidthUpdate)
      window.visualViewport?.removeEventListener('resize', scheduleWidthUpdate)
    }
  }, [columns, minTableWidth, visibleRows.length])

  useEffect(() => {
    const originalHeader = originalHeaderRef.current
    if (!originalHeader) return

    let frameId = 0
    const navHeader = document.querySelector('header')
    const container = parentRef.current

    const getStickyTop = () => {
      const navBottom = navHeader?.getBoundingClientRect().bottom ?? 0
      const boundaryBottom =
        stickyBoundaryRef?.current?.getBoundingClientRect().bottom ?? 0

      return Math.max(navBottom, boundaryBottom)
    }

    const setVisible = (visible: boolean) => {
      const floatingHeader = floatingHeaderRef.current
      if (!floatingHeader || visible === isFloatingVisibleRef.current) return

      isFloatingVisibleRef.current = visible

      if (visible) {
        floatingHeader.classList.remove(cn.floatingHeaderHide)
        floatingHeader.classList.add(cn.floatingHeaderVisible)
      } else {
        floatingHeader.classList.remove(cn.floatingHeaderVisible)
        floatingHeader.classList.add(cn.floatingHeaderHide)
      }
    }

    const updatePosition = () => {
      const floatingHeader = floatingHeaderRef.current
      if (!floatingHeader) return

      const top = getStickyTop()
      const tableRect = tableInnerRef.current?.getBoundingClientRect()

      floatingHeader.style.top = `${top}px`
      floatingHeader.style.left = `${tableRect?.left ?? 0}px`
      floatingHeader.style.width = `${tableRect?.width ?? 0}px`
    }

    const updateVisibility = () => {
      const tableRect = tableInnerRef.current?.getBoundingClientRect()
      const headerRect = originalHeader.getBoundingClientRect()
      const stickyTop = getStickyTop()

      const shouldShow =
        !!tableRect &&
        tableRect.bottom > stickyTop &&
        headerRect.top <= stickyTop

      setVisible(shouldShow)
    }

    const updateAll = () => {
      updatePosition()
      updateVisibility()
    }

    const scheduleUpdateAll = () => {
      cancelAnimationFrame(frameId)
      frameId = requestAnimationFrame(updateAll)
    }

    scheduleUpdateAll()

    const navResizeObserver = new ResizeObserver(scheduleUpdateAll)

    if (navHeader) {
      navResizeObserver.observe(navHeader)
    }

    if (stickyBoundaryRef?.current) {
      navResizeObserver.observe(stickyBoundaryRef.current)
    }

    const tableResizeObserver = new ResizeObserver(scheduleUpdateAll)

    if (tableInnerRef.current) {
      tableResizeObserver.observe(tableInnerRef.current)
    }

    const onScroll = () => scheduleUpdateAll()

    window.addEventListener('scroll', onScroll, { passive: true })
    container?.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', scheduleUpdateAll)
    window.visualViewport?.addEventListener('resize', scheduleUpdateAll)

    return () => {
      cancelAnimationFrame(frameId)
      navResizeObserver.disconnect()
      tableResizeObserver.disconnect()
      window.removeEventListener('scroll', onScroll)
      container?.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', scheduleUpdateAll)
      window.visualViewport?.removeEventListener('resize', scheduleUpdateAll)
    }
  }, [gridTemplateColumns, stickyBoundaryRef])

  const renderHeaderCells = () =>
    table.getHeaderGroups().map((headerGroup) =>
      headerGroup.headers.map((header) => (
        <div key={header.id} className={cn.headerCell}>
          {flexRender(header.column.columnDef.header, header.getContext())}
        </div>
      ))
    )

  if (isMobile && renderMobileCard) {
    return (
      <div className={cn.mobileCardsContainer}>
        {visibleRows.length === 0 ? (
          <div className={cn.emptyState}>{emptyMessage}</div>
        ) : (
          <>
            {visibleRows.map((record) => (
              <div
                key={rowKey(record)}
                className={cn.mobileCardWrapper}
                onClick={() => onRowClick?.(record)}
              >
                {renderMobileCard(record)}
              </div>
            ))}
          </>
        )}

        {hasMore && (
          <div ref={loadMoreRef} className={cn.loadMoreAnchor}>
            <Loader size={12} />
          </div>
        )}
      </div>
    )
  }

  return (
    <div ref={parentRef} className={cn.tableWrapper}>
      <div
        ref={tableInnerRef}
        className={cn.tableInner}
        style={{
          '--table-min-width': minTableWidth,
          '--table-width': tableWidth > 0 ? `${tableWidth}px` : minTableWidth,
          '--row-width': tableWidth > 0 ? `${tableWidth}px` : '100%',
          '--grid-columns': gridTemplateColumns,
        }}
      >
        <div ref={originalHeaderRef} className={cn.virtualHeader}>
          {renderHeaderCells()}
        </div>

        {createPortal(
          <div
            ref={floatingHeaderRef}
            className={cn.floatingHeader}
            style={{ '--grid-columns': gridTemplateColumns }}
          >
            {renderHeaderCells()}
          </div>,
          document.body
        )}

        {rows.length === 0 ? (
          <div className={cn.emptyState}>{emptyMessage}</div>
        ) : (
          <div
            className={cn.virtualList}
            style={{
              '--virtual-total-size': `${rowVirtualizer.getTotalSize()}px`,
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const row = rows[virtualRow.index]
              const record = row.original
              const key = rowKey(record)

              return (
                <div
                  key={key}
                  className={[cn.virtualRow, getRowClassName?.(record) || '']
                    .filter(Boolean)
                    .join(' ')}
                  style={{
                    '--virtual-row-size': `${virtualRow.size}px`,
                    '--virtual-row-start': `${virtualRow.start - rowVirtualizer.options.scrollMargin}px`,
                  }}
                  onClick={() => {
                    onRowClick?.(record)
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <div key={cell.id} className={cn.virtualCell}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </div>
                  ))}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {hasMore && (
        <div ref={loadMoreRef} className={cn.loadMoreAnchor}>
          <Loader size={12} />
        </div>
      )}
    </div>
  )
}

export default CatalogApprovalVirtualTable
